import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useWorldStore } from '../state/worldStore';

// useCallerFace — the face on the line (Khari, 2026-08-02: "when we are
// talking to anyone, their face should show").
//
// One sceneKey → one { name, image } from /api/dialogue/caller, which resolves
// the SAME avatar the voice path does — so the face on the screen belongs to
// the voice in your ear. Cached at module scope: the ring screen, the in-call
// header and the call log all ask about the same few callers, and a signed
// URL is good for far longer than a phone call.
//
// No face is a normal answer (an NPC without a portrait, the server napping) —
// callers fall back to the initial-letter disc they always had.

const cache = new Map();          // sceneKey|avatarId → { name, image }
const inflight = new Map();

function fetchFace(sceneKey, avatarId) {
  const k = `${sceneKey}|${avatarId || ''}`;
  if (cache.has(k)) return Promise.resolve(cache.get(k));
  if (inflight.has(k)) return inflight.get(k);
  const p = api.dialogueCaller(sceneKey, avatarId)
    .then((d) => {
      const face = { name: d?.name || null, image: d?.image || null };
      cache.set(k, face);
      return face;
    })
    .catch(() => ({ name: null, image: null }))
    .finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}

export function useCallerFace(sceneKey) {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const [face, setFace] = useState(() => (sceneKey ? cache.get(`${sceneKey}|${myAvatarId || ''}`)
    || cache.get(`${sceneKey}|`) || null : null));
  useEffect(() => {
    if (!sceneKey) { setFace(null); return undefined; }
    let dead = false;
    fetchFace(sceneKey, myAvatarId).then((f) => { if (!dead) setFace(f); });
    return () => { dead = true; };
  }, [sceneKey, myAvatarId]);
  return face;                     // null while loading → initial-letter disc
}

// The disc every call surface shares: the photo when there is one, the
// initial when there isn't (or while it loads). Size varies per surface.
export function faceDiscStyle(size) {
  return {
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(160deg,#ffb25e,#ff5e78)', color: '#1a0f00',
    display: 'grid', placeItems: 'center', font: `800 ${Math.round(size * 0.37)}px system-ui`,
    overflow: 'hidden',
  };
}

// The photo inside the disc. objectPosition anchors the CROP TO THE TOP:
// profile shots carry headroom, and a center crop drops the face low in the
// circle (Khari caught it on Lyra's). Top-anchored, the head owns the disc.
export const faceImgStyle = {
  width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top',
};
